import classNames from "classnames";
import * as React from "react";

import type { ResolvedDateTimeFormat } from "@/shared/datetime";

import { formatRange } from "./formatRange";
import type { Clip } from "./layout";
import styles from "./TimelineTracks.module.css";
import { percent, trackVars } from "./trackStyle";

interface TrackBarProps<T> {
  /** The item, its track, and its position as fractions of the window. */
  clip: Clip<T>;
  /** Draws the bar as the selection (outline, bolder label) and marks it pressed. */
  selected: boolean;
  /** Reads the item's start and end for its accessible name, on the timeline's clock. */
  format: ResolvedDateTimeFormat;
  /** A click on the bar; the timeline selects its item. */
  onClick: () => void;
  /** The bar, when a hover or focus begins; `null` when it ends. */
  onHover: (bar: HTMLButtonElement | null) => void;
}

/** One item's bar on its track. Selection is not colour alone: the bar also gets an outline and a bolder label. */
export function TrackBar<T>({
  clip,
  selected,
  format,
  onClick,
  onHover,
}: TrackBarProps<T>) {
  const { item } = clip;
  const range = formatRange(item, format);
  const begin = (event: React.SyntheticEvent<HTMLButtonElement>) =>
    onHover(event.currentTarget);
  const end = () => onHover(null);
  return (
    <button
      type="button"
      data-clip
      className={classNames(styles.bar, { [styles.barSelected]: selected })}
      style={{
        ...trackVars(clip.track),
        left: percent(clip.left),
        width: percent(clip.width),
      }}
      aria-label={`${item.label}, ${range.start} to ${range.end}`}
      aria-pressed={selected}
      onClick={onClick}
      onPointerEnter={begin}
      onPointerLeave={end}
      onFocus={begin}
      onBlur={end}
    >
      <span className={styles.barLabel}>{item.label}</span>
    </button>
  );
}
